import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";

const testimonials = [
  {
    name: "Sarah Chen",
    role: "VP of Customer Success",
    company: "TechFlow",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
    quote:
      "Pragma reduced our support costs by 62% while improving our response times. Their team integrated seamlessly with our processes—it feels like they're part of our company.",
    rating: 5,
    metrics: { savings: "62%", deployment: "14 business days" },
  },
  {
    name: "Michael Rodriguez",
    role: "Founder & CEO",
    company: "GrowthWorks",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    quote:
      "Best decision for our startup. Professional agents, no nonsense, deployed in 14 business days. Our customers love the support quality and we love the cost savings.",
    rating: 5,
    metrics: { savings: "58%", deployment: "14 business days" },
  },
  {
    name: "Emma Wilson",
    role: "Operations Manager",
    company: "CloudServe",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    quote:
      "The quality of support agents is exceptional. They handle complex technical tickets like our in-house team would—sometimes even better. Highly recommend.",
    rating: 5,
    metrics: { savings: "55%", deployment: "14 business days" },
  },
  {
    name: "David Kumar",
    role: "Head of Support",
    company: "DataFlow Inc",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    quote:
      "Pragma isn't just cheaper—they're better. Our customer satisfaction scores actually improved after switching. The real-time monitoring gives us complete visibility.",
    rating: 5,
    metrics: { savings: "60%", deployment: "14 business days" },
  },
];

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-950/5 to-transparent" />
      <div className="absolute top-1/4 left-0 w-64 h-64 border border-cyan-400/5 rounded-full -translate-x-1/2" />
      <div className="absolute bottom-1/4 right-0 w-64 h-64 border border-pink-500/5 rounded-full translate-x-1/2" />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block px-4 py-1.5 bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-sm font-mono rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Testimonials
          </motion.span>
          <h2 className="text-foreground mb-6">
            Trusted by <span className="text-cyan-400">Growing Companies</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            See what our partners say about working with Pragma.
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="relative p-8 md:p-12 border border-gray-800 bg-card/30 backdrop-blur-sm">
                {/* Quote Icon */}
                <Quote className="absolute top-6 left-6 w-12 h-12 text-cyan-400/20" />

                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-cyan-400 text-cyan-400"
                    />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-foreground text-xl md:text-2xl font-light leading-relaxed mb-8">
                  &ldquo;{testimonials[currentIndex].quote}&rdquo;
                </blockquote>

                {/* Author Info */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-foreground font-mono font-bold">
                        {testimonials[currentIndex].role}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        {testimonials[currentIndex].company}
                      </div>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="flex gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-mono font-bold text-cyan-400">
                        {testimonials[currentIndex].metrics.savings}
                      </div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider">
                        Cost Saved
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-mono font-bold text-cyan-400">
                        {testimonials[currentIndex].metrics.deployment}
                      </div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider">
                        Deployed
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <motion.button
              onClick={prevTestimonial}
              className="p-3 border border-border text-muted-foreground hover:border-cyan-400 hover:text-cyan-400 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </motion.button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? "w-8 bg-cyan-400"
                      : "bg-gray-600 hover:bg-gray-500"
                  }`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>

            <motion.button
              onClick={nextTestimonial}
              className="p-3 border border-border text-muted-foreground hover:border-cyan-400 hover:text-cyan-400 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}
